import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import * as esri from 'esri-leaflet';

// Polyfill: L.DomEvent.fakeStop was removed in Leaflet 1.9+ but esri-leaflet still uses it
if (!L.DomEvent.fakeStop) {
  L.DomEvent.fakeStop = function () { return L.DomEvent; };
}

/**
 * React-Leaflet wrapper for esri-leaflet FeatureLayer.
 * Renders ArcGIS FeatureServer services as vector overlays.
 *
 * IMPORTANT: This component is designed to be used inside a
 * <LayersControl.Overlay> + <LayerGroup>. It does NOT add itself
 * to the map on mount. Instead, it listens for the map's
 * overlayadd / overlayremove events and lazily creates the ESRI
 * layer only when the user enables the overlay checkbox.
 *
 * Props:
 *  - url:         ArcGIS FeatureServer REST URL (including /0 layer index if needed)
 *  - opacity:     Layer opacity (0-1), default 0.6
 *  - where:       SQL where clause for server-side filtering (optional)
 *  - style:       Leaflet path style function or object (optional)
 *  - minZoom:     Minimum zoom level to display features, default 6
 *  - attribution: Attribution string
 *  - overlayName: The name used in <LayersControl.Overlay name="..."> to match events
 */
function EsriFeatureLayer({ url, opacity = 0.6, where, style, minZoom = 6, attribution = '', overlayName }) {
  const map = useMap();
  const layerRef = useRef(null);

  // The `style` and `where` props are typically inline arrow functions
  // / fresh object literals created by the parent on every render. If
  // we put them in the effect's dependency array, every re-render of
  // the parent (which happens on every map pan, viewport fetch, or
  // filter change) would tear down the ESRI layer and re-register the
  // overlayadd/overlayremove listeners, silently hiding the overlay
  // until the user toggles it off and back on. Capture them in refs
  // so the effect can read the latest values without needing them as
  // dependencies.
  const styleRef = useRef(style);
  const whereRef = useRef(where);
  useEffect(() => { styleRef.current = style; }, [style]);
  useEffect(() => { whereRef.current = where; }, [where]);

  useEffect(() => {
    if (!url) return;

    function createLayer() {
      if (layerRef.current) return; // already created

      const options = {
        url,
        opacity,
        attribution,
        useCors: true,
        minZoom,
      };

      if (whereRef.current) {
        options.where = whereRef.current;
      }

      if (styleRef.current) {
        options.style = styleRef.current;
      }

      const layer = esri.featureLayer(options);
      layer.addTo(map);
      layerRef.current = layer;
    }

    function removeLayer() {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    }

    // Listen for LayersControl overlay events
    function onOverlayAdd(e) {
      if (overlayName && e.name === overlayName) {
        createLayer();
      }
    }

    function onOverlayRemove(e) {
      if (overlayName && e.name === overlayName) {
        removeLayer();
      }
    }

    map.on('overlayadd', onOverlayAdd);
    map.on('overlayremove', onOverlayRemove);

    return () => {
      map.off('overlayadd', onOverlayAdd);
      map.off('overlayremove', onOverlayRemove);
      removeLayer();
    };
  }, [map, url, opacity, minZoom, attribution, overlayName]);

  return null;
}

export default EsriFeatureLayer;
